import type { SortOrder } from "@cdt/shared";
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import type { PriorityReviewPreset } from "@/hooks/usePriorityReview";

const DEFAULT_PAGE_SIZE = 50;
const DEFAULT_SORT_ORDER: SortOrder = "asc";
const DEFAULT_PRESET: PriorityReviewPreset = "this_week";

export interface PriorityReviewFilters {
  preset: PriorityReviewPreset;
  page: number;
  pageSize: number;
  sortOrder: SortOrder;
}

export interface UsePriorityReviewFiltersResult extends PriorityReviewFilters {
  setPreset: (preset: PriorityReviewPreset) => void;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  setSortOrder: (sortOrder: SortOrder) => void;
}

function isPreset(value: string | null): value is PriorityReviewPreset {
  return value === "this_week" || value === "next_week" || value === "all";
}

/**
 * URL-bound filter state for the Priority Review page. Deliberately separate
 * from useEventsFilters/eventsQueryParams: this page only ever needs a small,
 * fixed set of params (preset/page/pageSize/sortOrder) and reusing the full
 * events query serialization would expose irrelevant filters (search,
 * categoryId, etc.) that don't apply here.
 */
export function usePriorityReviewFilters(): UsePriorityReviewFiltersResult {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo<PriorityReviewFilters>(() => {
    const presetParam = searchParams.get("preset");
    const pageParam = Number(searchParams.get("page"));
    const pageSizeParam = Number(searchParams.get("pageSize"));
    const sortOrderParam = searchParams.get("sortOrder");

    return {
      preset: isPreset(presetParam) ? presetParam : DEFAULT_PRESET,
      page: Number.isInteger(pageParam) && pageParam > 0 ? pageParam : 1,
      pageSize:
        Number.isInteger(pageSizeParam) && pageSizeParam > 0
          ? pageSizeParam
          : DEFAULT_PAGE_SIZE,
      sortOrder: sortOrderParam === "desc" ? "desc" : DEFAULT_SORT_ORDER,
    };
  }, [searchParams]);

  const applyPatch = useCallback(
    (patch: Partial<PriorityReviewFilters>) => {
      const next = { ...filters, ...patch };
      const params = new URLSearchParams();
      if (next.preset !== DEFAULT_PRESET) params.set("preset", next.preset);
      if (next.page !== 1) params.set("page", String(next.page));
      if (next.pageSize !== DEFAULT_PAGE_SIZE)
        params.set("pageSize", String(next.pageSize));
      if (next.sortOrder !== DEFAULT_SORT_ORDER)
        params.set("sortOrder", next.sortOrder);
      setSearchParams(params, { replace: false });
    },
    [filters, setSearchParams],
  );

  const setPreset = useCallback(
    (preset: PriorityReviewPreset) => applyPatch({ preset, page: 1 }),
    [applyPatch],
  );
  const setPage = useCallback(
    (page: number) => applyPatch({ page }),
    [applyPatch],
  );
  const setPageSize = useCallback(
    (pageSize: number) => applyPatch({ pageSize, page: 1 }),
    [applyPatch],
  );
  const setSortOrder = useCallback(
    (sortOrder: SortOrder) => applyPatch({ sortOrder, page: 1 }),
    [applyPatch],
  );

  return { ...filters, setPreset, setPage, setPageSize, setSortOrder };
}
